import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, 
  Search, 
  Users, 
  Phone, 
  Mail, 
  Building2,
  Car,
  MoreVertical,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  ChevronRight,
  MessageCircle
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientsPage() {
  const { user, isPro } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, clients, prospects
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    email: '',
    company: '',
    notes: '',
    is_prospect: false
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    
    setSaving(true);
    try {
      const url = editingClient 
        ? `${API_URL}/api/clients/${editingClient.client_id}`
        : `${API_URL}/api/clients`;
      
      // Clean form data: convert empty strings to null for optional fields
      const cleanedData = {
        name: formData.name,
        whatsapp: formData.whatsapp || null,
        email: formData.email || null,
        company: formData.company || null,
        notes: formData.notes || null,
        is_prospect: formData.is_prospect
      };
      
      const response = await fetch(url, {
        method: editingClient ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(cleanedData)
      });

      // Parse the JSON response
      let data;
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al guardar');
      }

      toast.success(editingClient ? 'Cliente actualizado' : 'Cliente creado');
      setShowForm(false);
      setEditingClient(null);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, clientId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!confirm('¿Estás seguro de eliminar este cliente? Se eliminarán también sus vehículos y servicios.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Error al eliminar');
      }

      toast.success('Cliente eliminado');
      fetchClients();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (e, client) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingClient(client);
    setFormData({
      name: client.name,
      whatsapp: client.whatsapp || '',
      email: client.email || '',
      company: client.company || '',
      notes: client.notes || '',
      is_prospect: client.is_prospect
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      whatsapp: '',
      email: '',
      company: '',
      notes: '',
      is_prospect: false
    });
  };

  const openWhatsApp = (e, phone) => {
    e.preventDefault();
    e.stopPropagation();
    if (!phone) return;
    const countryCode = user?.country_code || '+52';
    let cleanPhone = phone.replace(/\D/g, '');
    if (!cleanPhone.startsWith(countryCode.replace('+', ''))) {
      cleanPhone = countryCode.replace('+', '') + cleanPhone;
    }
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(search.toLowerCase()) ||
      client.email?.toLowerCase().includes(search.toLowerCase()) ||
      client.company?.toLowerCase().includes(search.toLowerCase());
    
    const matchesFilter = filter === 'all' || 
      (filter === 'clients' && !client.is_prospect) ||
      (filter === 'prospects' && client.is_prospect);
    
    return matchesSearch && matchesFilter;
  });

  const clientCount = clients.filter(c => !c.is_prospect).length;
  const canAddClient = isPro || clientCount < 5;

  return (
    <div className="animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
          <p className="text-slate-500">
            {clientCount} cliente{clientCount !== 1 ? 's' : ''}
            {!isPro && ` de 10 en plan gratuito`}
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingClient(null);
            setShowForm(true);
          }}
          className="bg-[#1E3A5F] hover:bg-[#162e4d]"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Cliente
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, email o empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-clients"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'clients', 'prospects'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f 
                  ? 'bg-[#1E3A5F] text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'Todos' : f === 'clients' ? 'Clientes' : 'Prospectos'}
            </button>
          ))}
        </div>
      </div>

      {/* Clients List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : filteredClients.length > 0 ? (
        <div className="space-y-3">
          {filteredClients.map((client) => (
            <Link
              key={client.client_id}
              to={`/clients/${client.client_id}`}
              className="card-base card-hover p-4 block cursor-pointer"
              data-testid={`client-${client.client_id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  client.is_prospect ? 'bg-slate-100' : 'bg-blue-100'
                }`}>
                  {client.is_prospect ? (
                    <UserX className="w-6 h-6 text-slate-500" />
                  ) : (
                    <UserCheck className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-900">
                      {client.name}
                    </span>
                    {client.is_prospect && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                        Prospecto
                      </span>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                    {client.whatsapp && (
                      <button 
                        onClick={(e) => openWhatsApp(e, client.whatsapp)}
                        className="flex items-center gap-1 hover:text-[#25D366]"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        {client.whatsapp}
                      </button>
                    )}
                    {client.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />
                        {client.email}
                      </span>
                    )}
                    {client.company && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5" />
                        {client.company}
                      </span>
                    )}
                    {!client.is_prospect && (
                      <span className="flex items-center gap-1">
                        <Car className="w-3.5 h-3.5" />
                        {client.vehicles_count} vehículo{client.vehicles_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
                      <Button variant="ghost" size="icon" data-testid={`client-menu-${client.client_id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => handleEdit(e, client)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => handleDelete(e, client.client_id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <ChevronRight className="w-5 h-5 text-slate-300" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <p className="text-slate-500 mb-4">
            {search || filter !== 'all' ? 'No se encontraron clientes' : 'No tienes clientes aún'}
          </p>
          {!search && filter === 'all' && (
            <Button onClick={() => setShowForm(true)} className="bg-[#1E3A5F]">
              <Plus className="w-4 h-4 mr-2" />
              Agregar primer cliente
            </Button>
          )}
        </div>
      )}

      {/* Client Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => {
        if (!open) {
          setShowForm(false);
          setEditingClient(null);
          resetForm();
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="client-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="whatsapp">WhatsApp (10 dígitos)</Label>
                <Input
                  id="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="5512345678"
                  maxLength={10}
                  data-testid="client-whatsapp-input"
                />
                <p className="text-xs text-slate-500 mt-1">Se usará código {user?.country_code || '+52'}</p>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="client-email-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="company">Empresa</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                data-testid="client-company-input"
              />
            </div>
            
            <div>
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="client-notes-input"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_prospect"
                checked={formData.is_prospect}
                onCheckedChange={(checked) => setFormData({ ...formData, is_prospect: checked })}
                data-testid="client-prospect-checkbox"
              />
              <Label htmlFor="is_prospect" className="text-sm font-normal cursor-pointer">
                Marcar como prospecto (no cuenta para el límite)
              </Label>
            </div>

            {/* Limit warning */}
            {!editingClient && !formData.is_prospect && !canAddClient && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Has alcanzado el límite de 5 clientes. Actualiza a PRO para clientes ilimitados o marca como prospecto.
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1E3A5F]" 
                data-testid="save-client-btn"
                disabled={saving || (!editingClient && !formData.is_prospect && !canAddClient)}
              >
                {saving ? 'Guardando...' : editingClient ? 'Guardar Cambios' : 'Crear Cliente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
